import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AppRoutes } from '@/constants/appRoutes'
import { useLogin, useSSO } from '@/hooks/Auth'
import { ELoginFrom, ELoginType, trackLoginViewedFrom } from '@/utils/analytics'
import type { LoginType } from '@/utils/schemas'
import { loginSchema } from '@/utils/schemas'

import { GoogleOauthButton } from './GoogleOauthButtom'

export const LoginForm = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const hasTrackedView = useRef(false)

  const { login, isLoading: isLoadingLogin } = useLogin()
  const { startGoogleSSO } = useSSO()

  useEffect(() => {
    if (hasTrackedView.current) return

    const typeParam = searchParams.get('type')?.toLowerCase()
    const fromParam = searchParams.get('from')?.toLowerCase()

    const isValidType = Object.values(ELoginType).includes(
      typeParam as ELoginType,
    )
    const isValidFrom = Object.values(ELoginFrom).includes(
      fromParam as ELoginFrom,
    )

    if (isValidType || isValidFrom) {
      trackLoginViewedFrom({
        ...(isValidType && { type: typeParam as ELoginType }),
        ...(isValidFrom && { from: fromParam as ELoginFrom }),
      })
    }

    hasTrackedView.current = true
  }, [searchParams])

  const form = useForm<LoginType>({
    resolver: zodResolver(loginSchema),

    defaultValues: {
      email: '',
      password: '',
    },
  })

  const { handleSubmit, control } = form

  const onSubmit = (data: any) => {
    login(data.password, data.email)
  }

  const handleGoogleLogin = () => {
    startGoogleSSO()
  }

  const [showLoginForm, setShowLoginForm] = useState(false)

  const handleToggleForm = () => {
    setShowLoginForm(!showLoginForm)
  }

  const typeParam = searchParams.get('type')?.toLowerCase() as ELoginType

  const getTitle = () => {
    if (typeParam === ELoginType.Visas) return t('auth.login.titles.vi')
    if (typeParam === ELoginType.Universities) return t('auth.login.titles.un')
    if (typeParam === ELoginType.Hiring) return t('auth.login.titles.hr')
    if (typeParam === ELoginType.WorldCup) return t('auth.login.titles.wc')
    return t('auth.login.title')
  }
  return (
    <div className="w-full h-full flex flex-col gap-8 items-center justify-center">
      <div className="flex flex-col gap-8 w-full max-w-[500px] bg-baseColorHighlight p-8 rounded-3xl">
        <div className="flex flex-col gap-6">
          <p className="text-[32px] max-sm:text-2xl font-medium text-center">
            {getTitle()}
          </p>

          <GoogleOauthButton
            variant="signin"
            onClick={handleGoogleLogin}
            isLoading={false}
          />
        </div>

        {showLoginForm && (
          <>
            <Form {...form}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-6"
              >
                <FormField
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="w-full bg-white rounded-md">
                      <FormControl className="w-full focus:outline-none">
                        <Input
                          {...field}
                          placeholder={t('auth.login.email')}
                          className="rounded-3xl"
                        />
                      </FormControl>
                      <FormMessage data-name="email" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="w-full bg-white rounded-md  ">
                      <FormControl className="w-full focus:outline-none">
                        <Input
                          {...field}
                          placeholder={t('auth.login.password')}
                          type="password"
                          showPassIcon
                          className="rounded-3xl"
                        />
                      </FormControl>
                      <FormMessage data-name="password" />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="font-medium tracking-widest"
                  isLoading={isLoadingLogin}
                >
                  {t('auth.login.button')}
                </Button>
              </form>
            </Form>
            <div className="text-center max-w-[436px]">
              <p className="pb-6 text-center text-neutralColor60 font-medium text-base hover:cursor-pointer hover:text-neutralColor30">
                <Link to={AppRoutes.forgotPassword}>
                  {t('auth.login.forgotPassword')}
                </Link>
              </p>
              <p className="text-center">
                <span className="text-neutralColor mr-1 text-base font-medium">
                  {t('auth.login.noAccount')}{' '}
                </span>
                <Link
                  to={AppRoutes.registerStart}
                  className="text-neutralColor60 text-base font-medium hover:cursor-pointer hover:text-neutralColor30"
                >
                  {t('auth.login.createOne')}
                </Link>
              </p>
            </div>
          </>
        )}
      </div>

      <div className="w-1/3">
        <button
          type="button"
          className="w-full text-center font-medium text-base text-neutralColor80 hover:text-neutralColor60 hover:cursor-pointer bg-transparent border-none"
          onClick={handleToggleForm}
        >
          {showLoginForm ? t('common.back') : t('auth.login.logInUsingEmail')}
        </button>
      </div>
    </div>
  )
}
